import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import investorStrategyService, { type StrategyEntry } from "../services/investorStrategyService";
import { productSearchService, type ProductSearchResponse } from "../services/productSearchService";
import type { ProductSearchItem } from "../types/productV3";
import { buildProductLookup, type ProductLookupKey, type StrategyDraftEntry } from "../utils/strategyUtils";
import { useAuth } from "./AuthContext";
import {
  clearStrategyDraftStorage,
  STRATEGY_DRAFT_STORAGE_KEY,
} from "./strategyDraftStorage";

export type { StrategyDraftEntry };

const SESSION_KEY = STRATEGY_DRAFT_STORAGE_KEY;
const STRATEGY_PRODUCTS_PAGE_SIZE = 1000;
const STRATEGY_PRODUCTS_FALLBACK_PAGE_SIZE = 100;

type RawProductSearchItem = Omit<ProductSearchItem, "performance"> & {
  performance?: ProductSearchItem["performance"];
};

interface StrategyDraftState {
  entries: StrategyDraftEntry[];
  savedEntries: StrategyDraftEntry[];
  isDirty: boolean;
  isInitializing: boolean;
  initError: string | null;
}

interface StrategyDraftContextValue {
  draft: StrategyDraftState;
  addProduct: (entry: Omit<StrategyDraftEntry, "weight">) => void;
  removeProduct: (entityType: string, productId: number) => void;
  updateWeight: (entityType: string, productId: number, weight: number) => void;
  isInDraft: (entityType: string, productId: number) => boolean;
  discardDraft: () => void;
  clearDraft: () => void;
  /** Replace the working draft entries (e.g. adopting an analyzer what-if mix). Does NOT touch savedEntries, so the Strategy page still diffs against the user's real saved strategy. */
  replaceDraftEntries: (entries: StrategyDraftEntry[]) => void;
  refreshSavedEntries: () => Promise<void>;
  markDraftSaved: (strategies: StrategyEntry[]) => void;
  totalWeight: number;
  minNlvMap: Map<ProductLookupKey, number>;
}

function entriesEqual(a: StrategyDraftEntry[], b: StrategyDraftEntry[]): boolean {
  if (a.length !== b.length) return false;
  const mapA = new Map<string, number>();
  for (const e of a) mapA.set(`${e.entityType}-${e.productId}`, e.weight);
  for (const e of b) {
    if (mapA.get(`${e.entityType}-${e.productId}`) !== e.weight) return false;
  }
  return true;
}

function persistEntries(entries: StrategyDraftEntry[]): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ entries }));
  } catch {
    // quota exceeded or private mode -- ignore
  }
}

function readStoredEntries(): StrategyDraftEntry[] | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.entries)) return parsed.entries;
    return null;
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
}

function buildDraftEntries(
  apiEntries: StrategyEntry[],
  lookup: ReadonlyMap<ProductLookupKey, { title: string }>,
  displayNameFallback = new Map<string, string>()
): StrategyDraftEntry[] {
  return apiEntries.map((e) => {
    const key = `${e.entityType}-${e.productId}` as ProductLookupKey;
    const weight = parseFloat(e.weight);
    return {
      entityType: e.entityType,
      productId: e.productId,
      weight: Number.isFinite(weight) ? weight : 0,
      displayName: lookup.get(key)?.title || displayNameFallback.get(key) || `${e.entityType} #${e.productId}`,
    };
  });
}

const StrategyDraftContext = createContext<StrategyDraftContextValue | null>(null);

// `active` defaults to true so every existing caller behaves exactly as before.
// Pass false to read the draft without triggering its (large) initial load --
// used by surfaces that resolve this hook but do not consume it, e.g. Discover
// in alpha mode, which reads the alpha draft instead.
export function useStrategyDraft(active: boolean = true): StrategyDraftContextValue {
  const ctx = useContext(StrategyDraftContext);
  if (!ctx) throw new Error("useStrategyDraft must be used within StrategyDraftProvider");
  const triggerInit = (ctx as StrategyDraftContextValue & { __triggerInit?: () => void }).__triggerInit;
  useEffect(() => {
    if (active) triggerInit?.();
  }, [active, triggerInit]);
  return ctx;
}

function isPageSizeRejected(err: unknown): boolean {
  return Boolean(
    err &&
      typeof err === "object" &&
      "status" in err &&
      (err as { status?: number }).status === 400
  );
}

function getProductKey(item: RawProductSearchItem): string {
  return `${item.productType}:${item.providerId}:${item.productId}`;
}

function shouldReplaceDedupedProduct(
  existing: RawProductSearchItem,
  candidate: RawProductSearchItem
): boolean {
  return existing.performance == null && candidate.performance != null;
}

function dedupeProducts(items: RawProductSearchItem[]): RawProductSearchItem[] {
  const byKey = new Map<string, RawProductSearchItem>();
  for (const item of items) {
    const key = getProductKey(item);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, item);
    } else if (shouldReplaceDedupedProduct(existing, item)) {
      byKey.set(key, item);
    }
  }
  return Array.from(byKey.values());
}

function normalizeProductSearchItem(item: RawProductSearchItem): ProductSearchItem {
  return {
    ...item,
    performance: item.performance ?? null,
  };
}

async function fetchFirstProductsPage(): Promise<{
  pageSize: number;
  response: ProductSearchResponse;
}> {
  try {
    return {
      pageSize: STRATEGY_PRODUCTS_PAGE_SIZE,
      response: await productSearchService.searchProducts({
        pageSize: STRATEGY_PRODUCTS_PAGE_SIZE,
      }),
    };
  } catch (err) {
    if (!isPageSizeRejected(err)) throw err;
    return {
      pageSize: STRATEGY_PRODUCTS_FALLBACK_PAGE_SIZE,
      response: await productSearchService.searchProducts({
        pageSize: STRATEGY_PRODUCTS_FALLBACK_PAGE_SIZE,
      }),
    };
  }
}

async function fetchAllProducts(): Promise<ProductSearchItem[]> {
  const first = await fetchFirstProductsPage();
  let items: RawProductSearchItem[] = first.response.items;
  if (first.response.totalPages > 1) {
    const pages = await Promise.all(
      Array.from({ length: first.response.totalPages - 1 }, (_, i) =>
        productSearchService.searchProducts({
          page: i + 2,
          pageSize: first.pageSize,
        })
      )
    );
    for (const p of pages) items = items.concat(p.items);
  }
  return dedupeProducts(items).map(normalizeProductSearchItem);
}

export const StrategyDraftProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<StrategyDraftEntry[]>([]);
  const [savedEntries, setSavedEntries] = useState<StrategyDraftEntry[]>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [minNlvMap, setMinNlvMap] = useState<Map<ProductLookupKey, number>>(new Map());
  const initRef = useRef(false);
  const requestRef = useRef(0);
  const { user, effectiveUserId } = useAuth();
  const ownerUserId = effectiveUserId === undefined
    ? user?.id ?? null
    : effectiveUserId;
  const ownerUserIdRef = useRef(ownerUserId);

  useLayoutEffect(() => {
    if (ownerUserIdRef.current === ownerUserId) return;
    ownerUserIdRef.current = ownerUserId;
    requestRef.current += 1;
    initRef.current = false;
    setEntries([]);
    setSavedEntries([]);
    setMinNlvMap(new Map());
    setInitError(null);
    setIsInitializing(false);
    clearStrategyDraftStorage();
  }, [ownerUserId]);

  const initialize = useCallback(async () => {
    if (initRef.current) return;
    initRef.current = true;
    const requestOwnerUserId = ownerUserIdRef.current;
    const requestId = ++requestRef.current;
    setIsInitializing(true);
    setInitError(null);

    try {
      const [strategyEntries, products] = await Promise.allSettled([
        investorStrategyService.getStrategies(),
        fetchAllProducts(),
      ]);

      if (requestId !== requestRef.current) return;

      if (strategyEntries.status === "rejected") {
        setInitError("Failed to load strategy. Please try again.");
        setIsInitializing(false);
        return;
      }

      if (products.status === "rejected") {
        setInitError("Failed to load product data. Please try again.");
        setIsInitializing(false);
        return;
      }

      const apiEntries = strategyEntries.value;
      const productItems = products.value;
      const lookup = buildProductLookup(productItems);

      const nlvMap = new Map<ProductLookupKey, number>();
      for (const [key, entry] of lookup) {
        if (entry.minNlv != null) nlvMap.set(key, entry.minNlv);
      }
      setMinNlvMap(nlvMap);

      const built = buildDraftEntries(apiEntries, lookup);

      setSavedEntries(built);

      const stored = readStoredEntries();
      if (stored) {
        setEntries(stored);
      } else {
        setEntries(built.map((e) => ({ ...e })));
      }
    } catch {
      setInitError("Failed to load strategy. Please try again.");
    } finally {
      if (
        requestId === requestRef.current
        && ownerUserIdRef.current === requestOwnerUserId
      ) {
        setIsInitializing(false);
      }
    }
  }, []);

  const addProduct = useCallback((entry: Omit<StrategyDraftEntry, "weight">) => {
    setEntries((prev) => {
      const exists = prev.some(
        (e) => e.entityType === entry.entityType && e.productId === entry.productId
      );
      if (exists) return prev;
      const next = [...prev, { ...entry, weight: 0 }];
      persistEntries(next);
      return next;
    });
  }, []);

  const removeProduct = useCallback((entityType: string, productId: number) => {
    setEntries((prev) => {
      const next = prev.filter(
        (e) => !(e.entityType === entityType && e.productId === productId)
      );
      persistEntries(next);
      return next;
    });
  }, []);

  const updateWeight = useCallback((entityType: string, productId: number, weight: number) => {
    setEntries((prev) => {
      const next = prev.map((e) =>
        e.entityType === entityType && e.productId === productId
          ? { ...e, weight }
          : e
      );
      persistEntries(next);
      return next;
    });
  }, []);

  const isInDraft = useCallback(
    (entityType: string, productId: number) =>
      entries.some((e) => e.entityType === entityType && e.productId === productId),
    [entries]
  );

  const discardDraft = useCallback(() => {
    setEntries(savedEntries.map((e) => ({ ...e })));
    clearStrategyDraftStorage();
  }, [savedEntries]);

  const clearDraft = useCallback(() => {
    setEntries([]);
    setSavedEntries([]);
    clearStrategyDraftStorage();
  }, []);

  const replaceDraftEntries = useCallback((newEntries: StrategyDraftEntry[]) => {
    const next = newEntries.map((e) => ({ ...e }));
    setEntries(next);
    persistEntries(next);
  }, []);

  const markDraftSaved = useCallback((strategies: StrategyEntry[]) => {
    if (ownerUserIdRef.current !== ownerUserId) return;
    requestRef.current += 1;
    const displayNameFallback = new Map<string, string>();
    for (const e of savedEntries) displayNameFallback.set(`${e.entityType}-${e.productId}`, e.displayName);
    for (const e of entries) displayNameFallback.set(`${e.entityType}-${e.productId}`, e.displayName);
    const built = buildDraftEntries(strategies, new Map(), displayNameFallback);
    setSavedEntries(built);
    setEntries(built.map((e) => ({ ...e })));
    clearStrategyDraftStorage();
  }, [entries, ownerUserId, savedEntries]);

  const refreshSavedEntries = useCallback(async () => {
    const requestOwnerUserId = ownerUserId;
    const requestId = ++requestRef.current;
    try {
      const [strategyEntries, products] = await Promise.allSettled([
        investorStrategyService.getStrategies(),
        fetchAllProducts(),
      ]);

      if (
        requestId !== requestRef.current
        || ownerUserIdRef.current !== requestOwnerUserId
      ) return;
      if (strategyEntries.status === "rejected") return;
      if (products.status === "rejected") return;

      const apiEntries = strategyEntries.value;
      const productItems = products.value;
      const lookup = buildProductLookup(productItems);

      const nlvMap = new Map<ProductLookupKey, number>();
      for (const [key, entry] of lookup) {
        if (entry.minNlv != null) nlvMap.set(key, entry.minNlv);
      }
      setMinNlvMap(nlvMap);

      const built = buildDraftEntries(apiEntries, lookup);

      setSavedEntries(built);
    } catch {
      // non-critical
    }
  }, [ownerUserId]);

  const isDirty = useMemo(() => {
    const effective = entries.filter((e) => e.weight > 0);
    return !entriesEqual(effective, savedEntries);
  }, [entries, savedEntries]);
  const totalWeight = useMemo(() => entries.reduce((s, e) => s + e.weight, 0), [entries]);

  const draft: StrategyDraftState = useMemo(
    () => ({ entries, savedEntries, isDirty, isInitializing, initError }),
    [entries, savedEntries, isDirty, isInitializing, initError]
  );

  const value = useMemo(() => {
    const ctx: StrategyDraftContextValue & { __triggerInit?: () => void } = {
      draft,
      addProduct,
      removeProduct,
      updateWeight,
      isInDraft,
      discardDraft,
      clearDraft,
      replaceDraftEntries,
      refreshSavedEntries,
      markDraftSaved,
      totalWeight,
      minNlvMap,
      __triggerInit: initialize,
    };
    return ctx;
  }, [
    draft, addProduct, removeProduct, updateWeight, isInDraft,
    discardDraft, clearDraft, replaceDraftEntries, refreshSavedEntries, markDraftSaved, totalWeight, minNlvMap, initialize,
  ]);

  return (
    <StrategyDraftContext.Provider value={value}>
      {children}
    </StrategyDraftContext.Provider>
  );
};

export default StrategyDraftContext;
